import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../../../services/api';

// İş aileleri (spec 2026-10-27 §7.1).
const useJobFamilyApi = (queryParams) => {
  const { t } = useTranslation();

  const [fetching, setFetching] = useState(true);
  const [data, setData] = useState([]);
  const [pagination, setPagination] = useState({ totalRowCount: 0, pageSize: 10, pageIndex: 0 });
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const lastValidQueryParams = useRef(null);

  useEffect(() => {
    const paramsToUse = queryParams && Object.keys(queryParams).length > 0
      ? queryParams
      : lastValidQueryParams.current || { pageNumber: 1, pageSize: 10 };
    lastValidQueryParams.current = paramsToUse;
    setFetching(true);
    // Ünvan/çalışan sayımları yalnız bu liste sayfasında istenir (seçiciler saymaz).
    api.get(`/v1/job-families?${new URLSearchParams({ ...paramsToUse, includeCounts: 'true' })}`)
      .then((res) => {
        const result = res.data?.data;
        const items = Array.isArray(result) ? result : (result?.data || []);
        setData(items);
        setPagination({
          totalRowCount: result?.totalRowCount || items.length,
          pageIndex: result?.pageIndex ? result.pageIndex - 1 : 0,
          pageSize: result?.pageSize || items.length || 10,
        });
      })
      .finally(() => setFetching(false));
  }, [queryParams, refreshTrigger]);

  const refresh = useCallback(() => setRefreshTrigger((p) => p + 1), []);

  const toPayload = (formData) => ({
    name: formData.name,
    code: formData.code || null,
    description: formData.description || null,
  });

  const create = useCallback(async (formData) => {
    try {
      const res = await api.post('/v1/job-families', toPayload(formData));
      if (!res.data?.status) return false;
      refresh();
      return true;
    } catch {
      return false;
    }
  }, [refresh]);

  const update = useCallback(async (id, formData) => {
    try {
      const res = await api.put(`/v1/job-families/${id}`, toPayload(formData));
      if (!res.data?.status) return false;
      refresh();
      return true;
    } catch {
      return false;
    }
  }, [refresh]);

  const _delete = useCallback(async (ids) => {
    try {
      if (ids.length === 1) await api.delete(`/v1/job-families/${ids[0]}`);
      else await api.post('/v1/job-families/batch-delete', ids);
      refresh();
      return true;
    } catch {
      return false;
    }
  }, [refresh]);

  const columns = useMemo(() => [
    { name: 'id', label: 'ID', options: { filter: false, sort: false, display: 'excluded' } },
    { name: 'name', label: t('jobFamily.name'), options: { filter: true, sort: false, filterType: 'textField', filterOptions: { defaultOp: 'like' } } },
    { name: 'code', label: t('jobFamily.code'), options: { filter: true, sort: false, filterType: 'textField', filterOptions: { defaultOp: 'like' } } },
    { name: 'titleCount', label: t('jobFamily.titleCount'), options: { filter: false, sort: false } },
    { name: 'employeeCount', label: t('jobFamily.employeeCount'), options: { filter: false, sort: false } },
  ], [t]);

  const objectSchema = useMemo(() => [
    { name: 'name', label: t('jobFamily.name'), type: 'text', grid: 12, required: true },
    { name: 'code', label: t('jobFamily.code'), type: 'text', grid: 12 },
    { name: 'description', label: t('jobFamily.description'), type: 'textarea', grid: 12 },
  ], [t]);

  return { objectSchema, columns, data, fetching, pagination, create, update, _delete };
};

export default useJobFamilyApi;
